import { Injectable, inject } from '@angular/core';
import { CmsApiService } from '../../core/services/api/cms-api.service';
import { CmsContentMapperService } from '../../core/services/cms-content-mapper.service';
import { CommonDataService } from '../../core/services/common-data.service';
import { UpdateOnLangChange } from '../../core/shared/decorators/update-on-lang-change';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, ReplaySubject, timer } from 'rxjs';
import { TimelineStatus, TimeService } from '../../core/services/time.service';
import { ITournament } from './tournaments.interface';
import { BreakpointsDetectorService } from '../../core/services/breakpoints-detector.service';
import { LanguageService } from '../../core/services/language.service';
import { ResponseHandlerService } from 'src/app/core/services/response-handler.service';
import { GameListConfig } from 'src/app/core/shared/components/game-list/game-list.component';
import { isArray, isNullOrUndefined } from 'ngx-unificator/helpers';
import { GamesCount } from './data/tournament.data';

@Injectable({
  providedIn: 'root',
})
export class TournamentsService {
  private _api = inject(CmsApiService);
  private _mapper = inject(CmsContentMapperService);
  private _data = inject(CommonDataService);
  private _time = inject(TimeService);
  private _lang = inject(LanguageService);
  private _breakPoint = inject(BreakpointsDetectorService);
  private _responseHandler = inject(ResponseHandlerService);

  /**
   * Current tournament
   */
  public tournament$: BehaviorSubject<ITournament> = new BehaviorSubject<ITournament>(null);

  public selectedGame: any;

  /**
   * Making polling requests in Game page to show tournament and leaders list
   */
  public tournamentForGame$: Observable<ITournament> = this.listPolling({ with_game_list: 1 });

  /**
   * Config for tournament games
   */
  public tournamentGamesConfig: GameListConfig;

  /**
   * Get promotions list from CMS and prepare for using in frontend
   *
   * @param params
   */
  @UpdateOnLangChange('tournament/list')
  list(params: object = {}): ReplaySubject<any> {
    return this._api.tournamentList(params).pipe(
      map(response =>
        this._mapper.mapCmsData(response.data.list, {
          id: 'id',
          slug: 'slug',
          startAt: 'startAt.date',
          endAt: 'endAt.date',
          publishAt: 'publishAt',
          unpublishAt: 'unpublishAt',
          gameList: 'gameList',
          isMultiTournament: 'isMultiTournament',
          identifier: 'identifier',
        }),
      ),
      map((list: ITournament[]) => list.filter(item => !isNullOrUndefined(item.gameList) && item?.gameList?.length)),
      map(list => this._mapTournamentList(list)),
      map(list => list.sort((a, b) => a.endAt - b.endAt)),
      map(list => {
        return {
          now: list.filter(item => item.tournamentTimeStatus === TimelineStatus.NOW),
          future: list.filter(item => item.tournamentTimeStatus === TimelineStatus.FUTURE),
          past: list.filter(item => item.tournamentTimeStatus === TimelineStatus.PAST),
        };
      }),
    ) as ReplaySubject<any>;
  }

  /**
   * Get promotions item from CMS and prepare for using in frontend
   *
   * @param params
   */
  @UpdateOnLangChange('tournament/item')
  item(params: object = {}): ReplaySubject<any> {
    return this._api.tournamentItem(params).pipe(
      tap(res => this._responseHandler.handleResponseStatus(res?.statusCode, '/tournaments')),
      map(response => {
        if (response.data) {
          return this._mapTournamentList(
            this._mapper.mapCmsData(response.data ? [response.data] : [], {
              id: 'id',
              slug: 'slug',
              startAt: 'startAt.date',
              endAt: 'endAt.date',
              identifier: 'identifier',
              gameList: 'gameList',
              isMultiTournament: 'isMultiTournament',
            }),
          );
        } else {
          return [
            {
              statusCode: response.statusCode,
            },
          ];
        }
      }),
    ) as ReplaySubject<any>;
  }

  /**
   * Get Tournament list from cache to make polling requests every 30 seconds and listen user changes, position in tournament.
   * Decorator @UpdateOnLangChange doesn`t need here
   * @param params
   */
  listPolling(params: object = {}): any {
    return timer(0, 30000).pipe(
      switchMap(() => this._api.tournamentList(params)),
      map(response =>
        this._mapper.mapCmsData(response.data.list, {
          id: 'id',
          slug: 'slug',
          startAt: 'startAt.date',
          endAt: 'endAt.date',
          publishAt: 'publishAt',
          unpublishAt: 'unpublishAt',
          identifier: 'identifier',
          gameList: 'gameList',
        }),
      ),
      map((list: ITournament[]) => list.filter(item => !isNullOrUndefined(item.gameList) && item?.gameList?.length)),
      map(list => this._mapTournamentList(list)),
      map(list => list.sort((a, b) => a.endAt - b.endAt)),
      map(list => {
        return {
          now: list.filter(item => item.tournamentTimeStatus === TimelineStatus.NOW),
        };
      }),
      map((data: any) => data.now),
      map((data: any) => data.filter(e => e.gameList.find(i => i.name === this.selectedGame?.name))[0]),
    );
  }

  /**
   * Map additional data for tournaments
   *
   * @param list
   * @private
   */
  private _mapTournamentList(list: Array<any>): Array<any> {
    if (!isArray(list)) {
      return null;
    }
    return list.map(tournament => {
      const startAt = this._time.toLocalDate(tournament.startAt);
      const endAt = this._time.toLocalDate(tournament.endAt);
      return {
        ...tournament,
        startAt: new Date(startAt),
        endAt: new Date(endAt),
        tournamentTimeStatus: this._time.resolveTimeStatus(startAt, endAt),
      };
    });
  }

  /**
   * Combine places with same prizes
   *
   * @param list
   * @private
   */
  private _mapPrizesList(list: any[]): any[] {
    const result = [];

    for (let i = 0; i < list.length; i++) {
      const wantedKey = (list[i].freespins_count || 0).toString() + list[i].money_award;
      const wantedIndex = list.lastIndexOf(
        list
          .slice()
          .reverse()
          .find(item => {
            return wantedKey === (item.freespins_count || 0).toString() + item.money_award;
          }),
      );

      const prize = {
        ...list[i],
        place: wantedIndex !== i ? list[i].award_place + '-' + list[wantedIndex].award_place : list[i].award_place,
        place_suffix: wantedIndex !== i ? '' : ['st', 'nd', 'rd'][((((list[i].award_place + 90) % 100) - 10) % 10) - 1] || 'th',
        multiple: wantedIndex !== i,
      };

      result.push(prize);

      if (wantedIndex > i) {
        i = wantedIndex;
      }
    }

    return result;
  }

  /**
   * Mapped winners and set current user position
   * @param data
   * @param status
   * @private
   */
  mapTopPlayers(data: any, status: any) {
    const topPlayers = data?.top_players;

    if (!status?.award_place) {
      return topPlayers;
    }

    let players;

    if (!topPlayers.some(player => player.award_place === status.award_place)) {
      status.current_user = true;
      players = [...topPlayers, status];
    } else {
      topPlayers.forEach(player => {
        if (player.award_place === status.award_place) {
          player.current_user = true;
        }
      });
      players = [...topPlayers];
    }
    return players.sort((a, b) => a.award_place - b.award_place);
  }

  public getTournamentBySlug$(slug: string): Observable<any> {
    return this.item({ slug }).pipe(
      filter(data => !!data),
      map(data => data[0]),
      tap((tournament: ITournament) => {
        this.tournament$.next(tournament);
        this.tournamentGamesConfig = {
          link: `${this._lang.current}/tournaments/${tournament.slug}/games`,
          type: 'tournament',
          filter: { limit: this._setGamesLimit(tournament) },
          title: 'link.games',
          gameList: tournament?.gameList,
        };
      }),
    );
  }

  /**
   * Set games limit
   * @param tournament
   * @private
   */
  private _setGamesLimit(tournament: any) {
    switch (true) {
      case tournament?.gameList?.length < GamesCount.DESKTOP && this._breakPoint.isDesktop:
        return tournament?.gameList?.length;
      case tournament?.gameList?.length >= GamesCount.DESKTOP && this._breakPoint.isDesktop:
        return GamesCount.DESKTOP;
      case tournament?.gameList?.length < GamesCount.MOBILE && this._breakPoint.isMobile:
        return tournament?.gameList?.length;
      case tournament?.gameList?.length >= GamesCount.MOBILE && this._breakPoint.isMobile:
        return GamesCount.MOBILE;
    }
  }
}
